/**
 * SavedPostsContext — Context provider for the Saved Posts screen.
 *
 * Responsibilities:
 *  • Loads saved posts via feedService.getSavedPosts
 *  • Tracks activeTab and exposes filteredPosts derived from it
 *  • Exposes like / unsave / comment-count / delete / block helpers for optimistic UI
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import feedService from '../api/services/feedService';
import { getReactionDefinition } from '../constants/reactions';
import {
  incrementPostShareCount,
  useFeedShareCountSync,
} from '../hooks/useFeedShareCountSync';
import type { FeedPost } from '../types/post';
import { getUserFacingErrorMessage } from '../utils/apiErrorHandler';
import { useTranslation } from '../i18n/useTranslation';

/** FeedPost already carries the media fields the saved list renders. */
export type SavedPost = FeedPost;

export interface SavedPostsContextType {
  posts: SavedPost[];
  filteredPosts: SavedPost[];
  activeTab: string;
  setActiveTab: (tab: string) => void;
  loading: boolean;
  error: string | null;
  /** True once a request answered, so "nothing saved yet" is only shown then. */
  loaded: boolean;
  likePost: (postId: string, reactionTypeId?: number) => Promise<void>;
  /** Unsave via API and remove from the Saved Posts list. */
  removePost: (postId: string) => Promise<void>;
  /** Remove from list only (e.g. after delete) — no Save API. */
  deleteLocalPost: (postId: string) => void;
  /** Applies a partial change to one cached post. */
  patchPost: (postId: string, patch: Partial<SavedPost>) => void;
  updateCommentCount: (postId: string, delta: number) => void;
  /** Mark author blocked and drop their posts from the list. */
  applyAuthorBlock: (userId?: string | number | null, username?: string) => void;
  refresh: () => void;
}

const SavedPostsContext = createContext<SavedPostsContextType | null>(null);

export const SavedPostsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const [posts, setPosts] = useState<SavedPost[]>([]);
  const [activeTab, setActiveTab] = useState('All');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const fetchSavedPosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const pageResult = await feedService.getSavedPosts(1, 50);
      setPosts(pageResult.data as SavedPost[]);
      setLoaded(true);
    } catch (err) {
      setError(getUserFacingErrorMessage(err, t('common.errors.generic')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSavedPosts();
  }, [fetchSavedPosts]);

  const filteredPosts = useMemo(() => {
    switch (activeTab) {
      case 'Text':
        return posts.filter(p => !p.image && !p.video && !p.groupId);
      case 'Photos':
        return posts.filter(p => !!p.image || (Array.isArray(p.images) && p.images.length > 0));
      case 'Videos':
        return posts.filter(p => !!p.video);
      case 'Groups':
        return posts.filter(p => !!p.groupId);
      case 'All':
      default:
        return posts;
    }
  }, [posts, activeTab]);

  const likePost = useCallback(async (postId: string, reactionTypeId?: number) => {
    let previous: SavedPost | undefined;
    let targetReactionTypeId: number = 1;
    setPosts(prev => {
      previous = prev.find(p => p.id === postId);
      return prev.map(post => {
        if (post.id !== postId) return post;
        const currentReactionId = post.reactionTypeId ?? (post.liked ? 1 : null);
        let nextLiked: boolean;
        let nextLikes: number;
        let nextReactionTypeId: number | null;
        let nextReactionName: string | null;

        if (reactionTypeId == null) {
          nextLiked = !post.liked;
          nextLikes = nextLiked ? (post.likes || 0) + 1 : Math.max(0, (post.likes || 0) - 1);
          nextReactionTypeId = nextLiked ? 1 : null;
          nextReactionName = nextLiked ? 'Like' : null;
        } else if (post.liked && currentReactionId === reactionTypeId) {
          nextLiked = false;
          nextLikes = Math.max(0, (post.likes || 0) - 1);
          nextReactionTypeId = null;
          nextReactionName = null;
        } else if (post.liked) {
          nextLiked = true;
          nextLikes = post.likes || 0;
          const def = getReactionDefinition(reactionTypeId);
          nextReactionTypeId = reactionTypeId;
          nextReactionName = def?.name ?? null;
        } else {
          nextLiked = true;
          nextLikes = (post.likes || 0) + 1;
          const def = getReactionDefinition(reactionTypeId);
          nextReactionTypeId = reactionTypeId;
          nextReactionName = def?.name ?? null;
        }

        targetReactionTypeId = nextLiked
          ? (nextReactionTypeId ?? 1)
          : (currentReactionId ?? reactionTypeId ?? 1);

        return {
          ...post,
          liked: nextLiked,
          likes: nextLikes,
          reactionTypeId: nextReactionTypeId,
          reactionName: nextReactionName,
        };
      });
    });
    try {
      const res = await feedService.likePost(postId, {
        currentlyLiked: previous?.liked ?? false,
        reactionTypeId: targetReactionTypeId,
      });
      if (!res.success && previous) {
        setPosts(prev =>
          prev.map(post => (post.id === postId ? previous! : post)),
        );
      }
    } catch {
      if (previous) {
        setPosts(prev =>
          prev.map(post => (post.id === postId ? previous! : post)),
        );
      }
    }
  }, []);

  const removePost = useCallback(async (postId: string) => {
    let removed: SavedPost | undefined;
    setPosts(prev => {
      removed = prev.find(p => p.id === postId);
      return prev.filter(p => p.id !== postId);
    });
    try {
      const res = await feedService.savePost(postId, false);
      if (!res.success && removed) {
        setPosts(prev => [removed!, ...prev]);
      }
    } catch {
      if (removed) {
        setPosts(prev => [removed!, ...prev]);
      }
    }
  }, []);

  const deleteLocalPost = useCallback((postId: string) => {
    setPosts(prev => prev.filter(p => p.id !== postId));
  }, []);

  const patchPost = useCallback((postId: string, patch: Partial<SavedPost>) => {
    setPosts(prev =>
      prev.map(post => (post.id === postId ? { ...post, ...patch } : post)),
    );
  }, []);

  const updateCommentCount = useCallback((postId: string, delta: number) => {
    setPosts(prev =>
      prev.map(post =>
        post.id !== postId
          ? post
          : {
              ...post,
              comments: Math.max(0, (post.comments || 0) + delta),
            },
      ),
    );
  }, []);

  const applyAuthorBlock = useCallback(
    (userId?: string | number | null, username?: string) => {
      setPosts(prev =>
        prev.filter(post => {
          const matchUser =
            (userId != null && String(post.userId) === String(userId)) ||
            (username && post.username === username);
          return !matchUser;
        }),
      );
    },
    [],
  );

  const refresh = useCallback(() => {
    fetchSavedPosts();
  }, [fetchSavedPosts]);

  const handleShareCountSync = useCallback((postId: string) => {
    setPosts(prev => incrementPostShareCount(prev, postId));
  }, []);

  useFeedShareCountSync(handleShareCountSync);

  const value = useMemo(
    () => ({
      posts,
      filteredPosts,
      activeTab,
      setActiveTab,
      loading,
      error,
      loaded,
      likePost,
      removePost,
      deleteLocalPost,
      patchPost,
      updateCommentCount,
      applyAuthorBlock,
      refresh,
    }),
    [
      posts,
      filteredPosts,
      activeTab,
      loading,
      error,
      loaded,
      likePost,
      removePost,
      deleteLocalPost,
      patchPost,
      updateCommentCount,
      applyAuthorBlock,
      refresh,
    ],
  );

  return (
    <SavedPostsContext.Provider value={value}>
      {children}
    </SavedPostsContext.Provider>
  );
};

export const useSavedPosts = () => {
  const context = useContext(SavedPostsContext);
  if (!context) {
    throw new Error('useSavedPosts must be used within a SavedPostsProvider');
  }
  return context;
};

export default SavedPostsContext;
